import { z } from 'zod';
import {
  PaymentReceiptsTypes,
  PixKeyType,
  PurchaseReceiptsTypes,
  TypeOfBankTransfer,
} from './interfaces';
import { SupplierType } from '@/data/subsidy/interfaces';
import { isValidCPF } from '@/utils/form-validators';
import { isValidCNPJ } from '@/utils/form-validators/cnpj';

const phoneRegex = /^\+?[1-9]\d{10,14}$/;
const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const dateSchema = (fieldName: string) =>
  z
    .string({ message: `${fieldName} obrigatória` })
    .min(1, { message: `${fieldName} obrigatória` })
    .refine((date) => {
      const match = date.match(dateRegex);
      if (!match) return false;

      const [, day, month, year] = match;
      const dateObj = new Date(+year, +month - 1, +day);

      return (
        dateObj.getDate() === +day &&
        dateObj.getMonth() === +month - 1 &&
        dateObj.getFullYear() === +year
      );
    }, 'Data inválida')
    .refine((date) => {
      const [, day, month, year] = date.match(dateRegex)!;
      const dateObj = new Date(+year, +month - 1, +day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      dateObj.setHours(0, 0, 0, 0);
      return dateObj.getTime() <= today.getTime();
    }, 'Data deve ser menor ou igual a hoje');

const amountSchema = (fieldName: string) =>
  z
    .number({ message: `${fieldName} obrigatório` })
    .positive('Valor deve ser maior que zero');

const maxMbFileSize = 2;

const requiredFileArraySchema = z
  .array(z.instanceof(File), { message: 'Pelo menos um arquivo é necessário.' })
  .min(1, 'Pelo menos um arquivo é necessário.')
  .refine(
    (files) => files.every((f) => f.size / 1024 ** 2 <= maxMbFileSize),
    'Arquivo deve ter no máximo 2MB.'
  );

const optionalFileArraySchema = z
  .array(z.instanceof(File))
  .optional()
  .refine(
    (files) =>
      !files || files.every((f) => f.size / 1024 ** 2 <= maxMbFileSize),
    'Arquivo deve ter no máximo 2MB.'
  );

const supplierBaseSchema = {
  supplier_name: z
    .string({ message: 'Nome do fornecedor obrigatório' })
    .min(1, 'Nome do fornecedor obrigatório'),
};

const legalSupplierSchema = z.object({
  ...supplierBaseSchema,
  supplier_type: z.literal(SupplierType.LegalPerson),
  supplier_document: z
    .string({ message: 'CNPJ obrigatório' })
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => /^\d{14}$/.test(v), 'CNPJ deve conter 14 dígitos')
    .refine(isValidCNPJ, 'CNPJ inválido'),
});

const naturalSupplierSchema = z.object({
  ...supplierBaseSchema,
  supplier_type: z.literal(SupplierType.NaturalPerson),
  supplier_document: z
    .string({ message: 'CPF obrigatório' })
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => /^\d{11}$/.test(v), 'CPF deve conter 11 dígitos')
    .refine(isValidCPF, 'CPF inválido'),
});

const internationalSupplierSchema = z.object({
  ...supplierBaseSchema,
  supplier_type: z.literal(SupplierType.InternationalSupplier),
  supplier_document: z.string().optional(),
});

const supplierSchema = z.discriminatedUnion('supplier_type', [
  legalSupplierSchema,
  naturalSupplierSchema,
  internationalSupplierSchema,
]);

const pixSchema = z
  .object({
    bank_transfer_type: z.literal(TypeOfBankTransfer.Pix),
    pix_key_type: z.enum([
      PixKeyType.Cpf,
      PixKeyType.Email,
      PixKeyType.Phone,
      PixKeyType.Random,
    ]),
    pix_key: z.string().optional().default(''),
  })
  .superRefine((data, ctx) => {
    if (data.pix_key_type === PixKeyType.Cpf) return;

    const value = (data.pix_key ?? '').trim();
    const clean = value.replace(/\D/g, '');

    if (!value) {
      ctx.addIssue({
        path: ['pix_key'],
        message: 'Chave PIX obrigatória',
        code: 'custom',
      });
      return;
    }

    switch (data.pix_key_type) {
      case PixKeyType.Email:
        if (!z.email().safeParse(value).success) {
          ctx.addIssue({
            path: ['pix_key'],
            message: 'E-mail inválido',
            code: 'custom',
          });
        }
        break;

      case PixKeyType.Phone:
        if (!phoneRegex.test(clean)) {
          ctx.addIssue({
            path: ['pix_key'],
            message: 'Telefone inválido',
            code: 'custom',
          });
        }
        break;

      case PixKeyType.Random:
        if (
          !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            value
          )
        ) {
          ctx.addIssue({
            path: ['pix_key'],
            message: 'Chave aleatória inválida',
            code: 'custom',
          });
        }
    }
  });

const tedSchema = z.object({
  bank_transfer_type: z.literal(TypeOfBankTransfer.Ted),
  bank: z.string({ message: 'Banco obrigatório' }).min(1, 'Banco obrigatório'),
  agency: z
    .string({ message: 'Agência obrigatória' })
    .min(1, 'Agência obrigatória'),
  account_number: z
    .string({ message: 'Número da conta obrigatório' })
    .min(1, 'Número da conta obrigatório'),
});

const holderSchema = z.object({
  holder_name: z
    .string({ message: 'Nome completo obrigatório' })
    .min(1, 'Nome completo obrigatório'),
});

const bankSchema = z.discriminatedUnion('bank_transfer_type', [
  pixSchema,
  tedSchema,
]);

const receiptsSchema = z.object({
  receiver_fiscal_document: z
    .string({ message: 'CPF obrigatório' })
    .transform((v) => v.replace(/\D/g, ''))
    .refine((v) => /^\d{11}$/.test(v), 'CPF inválido')
    .refine(isValidCPF, 'CPF inválido'),
  purchase_receipt_type: z.enum(PurchaseReceiptsTypes, {
    message: 'Tipo de comprovante obrigatório',
  }),
  purchase_date: dateSchema('Data da compra'),
  purchase_amount: amountSchema('Valor da compra'),
  payment_receipt_type: z.enum(PaymentReceiptsTypes, {
    message: 'Tipo de comprovante obrigatório',
  }),
  payment_date: dateSchema('Data do pagamento'),
  payment_amount: amountSchema('Valor do pagamento'),
  reference_month: z.number().optional(),
});

const uploadFilesSchema = z.object({
  purchase_attachments: requiredFileArraySchema,
  payment_attachments: requiredFileArraySchema,
  optional_files: optionalFileArraySchema,
});

export const formValidationSchema = supplierSchema
  .and(receiptsSchema)
  .and(holderSchema)
  .and(bankSchema)
  .and(uploadFilesSchema);

export type InvoiceFormSchema = z.infer<typeof formValidationSchema>;
